import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CATEGORY_META, getCategoryMeta, SEVERITY_COLOR, formatNotifTime } from '../lib/notifications';
import { Bell, CheckCheck } from 'lucide-react';

const PAGE_SIZE = 20;

const FILTERS = [
  { key: 'all', label: 'All' },
  ...Object.entries(CATEGORY_META).map(([key, meta]) => ({ key, label: meta.label })),
];

const NotificationsPage = () => {
  const { getAuthHeaders, API_URL } = useAuth();
  const { refresh: refreshBell } = useNotifications();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [category, setCategory] = useState('all');
  const [unreadOnly, setUnreadOnly] = useState(false);

  const buildUrl = useCallback((skip) => {
    const params = new URLSearchParams({ limit: String(PAGE_SIZE), skip: String(skip) });
    if (category !== 'all') params.set('category', category);
    if (unreadOnly) params.set('unread_only', 'true');
    return `${API_URL}/api/notifications?${params.toString()}`;
  }, [API_URL, category, unreadOnly]);

  const fetchPage = useCallback(async (skip, append) => {
    try {
      const res = await axios.get(buildUrl(skip), { headers: getAuthHeaders() });
      setItems((prev) => (append ? [...prev, ...res.data] : res.data));
      setHasMore(res.data.length === PAGE_SIZE);
    } catch (error) {
      // non-critical
    }
  }, [buildUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setLoading(true);
    fetchPage(0, false).finally(() => setLoading(false));
  }, [category, unreadOnly, fetchPage]);

  const loadMore = async () => {
    setLoadingMore(true);
    await fetchPage(items.length, true);
    setLoadingMore(false);
  };

  const handleClick = async (notif) => {
    if (!notif.read) {
      try {
        await axios.post(`${API_URL}/api/notifications/${notif.id}/read`, {}, { headers: getAuthHeaders() });
      } catch (error) { /* ignore */ }
      setItems((prev) => prev.map((n) => (n.id === notif.id ? { ...n, read: true } : n)));
      refreshBell();
    }
    if (notif.link) navigate(notif.link);
  };

  const handleMarkAll = async () => {
    try {
      await axios.post(`${API_URL}/api/notifications/read-all`, {}, { headers: getAuthHeaders() });
    } catch (error) { /* ignore */ }
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    refreshBell();
    if (unreadOnly) fetchPage(0, false);
  };

  return (
    <DashboardLayout>
      <div className="space-y-5 max-w-3xl">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-2xl font-bold text-[#1A1A1A] flex items-center gap-2">
              <Bell className="w-6 h-6 text-[#ef785b]" /> Notifications
            </h2>
            <p className="text-[#64748B] text-sm mt-1">Everything happening across your account.</p>
          </div>
          <Button variant="outline" size="sm" onClick={handleMarkAll}>
            <CheckCheck className="w-4 h-4 mr-1.5" /> Mark all read
          </Button>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 flex-wrap">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setCategory(f.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  category === f.key ? 'bg-[#ef785b] text-white' : 'bg-white border border-[#E2E2DF] text-[#64748B] hover:bg-[#FAFAF8]'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-1.5 text-xs text-[#64748B] ml-auto cursor-pointer">
            <input type="checkbox" checked={unreadOnly} onChange={(e) => setUnreadOnly(e.target.checked)} className="accent-[#ef785b]" />
            Unread only
          </label>
        </div>

        {/* List */}
        {loading ? (
          <div className="text-center py-12 text-[#64748B]">Loading...</div>
        ) : items.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-[#64748B]">
            <Bell className="w-10 h-10 mx-auto mb-3 text-[#CBD5E1]" />
            No notifications {unreadOnly ? 'unread' : 'here'} yet.
          </CardContent></Card>
        ) : (
          <Card>
            <CardContent className="p-0 divide-y divide-[#F0F0EE]">
              {items.map((notif) => {
                const meta = getCategoryMeta(notif.category);
                const Icon = meta.icon;
                const tint = SEVERITY_COLOR[notif.severity] || meta.color;
                return (
                  <button
                    key={notif.id}
                    onClick={() => handleClick(notif)}
                    className={`w-full text-left flex items-start gap-3 p-4 hover:bg-[#FAFAF8] ${notif.read ? '' : 'bg-[#ef785b]/5'}`}
                  >
                    <span className="mt-0.5 shrink-0 w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: `${tint}1A` }}>
                      <Icon className="w-4.5 h-4.5" style={{ color: tint, width: 18, height: 18 }} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-[#1A1A1A] truncate">{notif.title}</p>
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#F1F5F9] text-[#64748B] capitalize shrink-0">{meta.label}</span>
                      </div>
                      <p className="text-sm text-[#475569] mt-0.5">{notif.body}</p>
                      <p className="text-[11px] text-[#94A3B8] mt-1">
                        {formatNotifTime(notif.created_at)}{notif.actor_name ? ` · by ${notif.actor_name}` : ''}
                      </p>
                    </div>
                    {!notif.read && <span className="w-2.5 h-2.5 rounded-full bg-[#ef785b] mt-1.5 shrink-0" />}
                  </button>
                );
              })}
            </CardContent>
          </Card>
        )}

        {hasMore && !loading && (
          <div className="text-center">
            <Button variant="outline" onClick={loadMore} disabled={loadingMore}>
              {loadingMore ? 'Loading...' : 'Load more'}
            </Button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default NotificationsPage;
